type TFranchiseVehicleType = "New" | "Pre-owned";

interface IBasicUnitProperties {
	_id: string;
	vin: string;
	year: number;
	make: string;
	model: string;
	color: string;
}

interface IUnitOnLot extends IBasicUnitProperties {
	type: TFranchiseVehicleType | "Loaners" | "Rentals";
	lot: string;
	stock: string;
	balance: number;
}
interface IUnitOnSchedule extends IBasicUnitProperties {
	type: Omit<TFranchiseVehicleType, "Pre-owned"> | "Loaners" | "Rentals";
	lot: string;
	serial: string;
	balance: number;
}

interface IUnitOnMfg extends IBasicUnitProperties {}

interface ISummaryDetail {
	storeNumber: string;
	inventoryDate: string;
	reconciliationCompletionDate: string;
	dealership: string;
	// Below are the common properties for all types of vehicles (New and Used)
	physicalInventoryCountOnLot: number;
	physicalInventoryCountOffLot: number;
	unitsOnScheduleNotOnLot: number;
	unitsOnLotNotOnSchedule: number;
	unitsOffLotNotOnSchedule: number;
	perGeneralLedgerSchedule: number;
	totalSchedule: number;
	differenceSchedule: number;
	// Below are the properties for New vehicles
	physicalInventoryOnLot: number;
	physicalInventoryOffLot: number;
	unitsInPhysicalInventoryNotOnMfg: number;
	unitsOffPhysicalInventoryNotOnMfg: number;
	perManufacturerReport: number;
	totalManufacture: number;
	differenceManufacture: number;

	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	vehicleType: string;
}

interface IBasicUnitExtended extends IBasicUnitProperties {
	type: TFranchiseVehicleType | "Loaners" | "Rentals";
	status: string;
	reason: string;
	resolution: string;
	notes: IReconciliationNote;
}

interface IOnScheduledNotOnLot extends IBasicUnitExtended {
	serial: string;
	balance: number;
}

interface IOnLotNotOnScheduled extends IBasicUnitExtended {
	lot: string;
}

interface IOnLotNotOnMfg extends IBasicUnitExtended {
	lot: string;
}

interface IOnMfgNotOnLot extends IBasicUnitExtended {}

interface IReconciliationNote {
	_id: string;
	reconciliationId: string;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	notes: IReconciliationNoteHistory[];
}

interface IReconciliationNoteHistory {
	_id: string;
	note: string;
	createdBy: string;
	createdByFullName: string;
	createdAt: Date;
}

interface IReconciliation {
	_id: string;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	createdAt: Date;
	reconciliationDate: Date;
	updatedAt: Date;
	updatedBy: string;
	vehicle: IVehicleInfo;
	reason: string;
	resolution: string;
	status: string;
	reconciled: boolean;
	manuallyReconciled: boolean;
	hasNotes: boolean;
	notes: IReconciliationNote;
	keyStates: [IKeyStates];
	inventoryId: string;
	dmsId: string;
	gpsId: string;
	kmsAssetId: string;
	lot: ILotInfo;
	balance: number;
	gps?: IGpsInfo;
}

interface IReconciliationStatus {
	_id: string;
	commissionCycleId: string;
	locationId: string;
	payPeriodId: string;
	newVehicleReconciliation: IReconciliationStatusType;
	usedVehicleReconciliation: IReconciliationStatusType;
}
interface IReconciliationStatusType {
	status: string;
	endDate: Date | null;
	startDate: Date | null;
	error: string;
}

type TReconciliationStatusType = Pick<IReconciliationStatus, "usedVehicleReconciliation" | "newVehicleReconciliation">;

interface IReconciliationReason {
	_id: string;
	reason: string;
}

interface IReconciliationResolution {
	_id: string;
	resolution: string;
}

interface IGpsInfo {
	address: string;
	gpsVehicleType: string;
}

interface IVehicleInfo {
	_id: string;
	stockNumber: string;
	vinNumber: string;
	year: string;
	make: string;
	model: string;
	trim: string;
	color: string;
	type: string;
	status: string;
}

const VEHICLE_INFO_PROPS = ["stockNumber", "vinNumber", "year", "make", "model", "trim", "color", "status"];
const SPECIAL_VEHICLE_TYPES = ["PARTS", "SHUTTLE", "LOANER", "RENTAL"];

interface IKeyStates {
	_id: string;
	kioskId: string;
	isConsistent: boolean;
	lastEvent: string;
	createdBy: string;
	userId: string;
	creationDate: Date;
	completionDate: Date;
}

const KEY_STATES_PROPS = ["kioskId", "isConsistent", "lastEvent", "createdBy", "userId", "creationDate", "completionDate"];

interface ILotInfo {
	_id: string;
	name: string;
	address: string;
	locationId: string;
}

const LOT_INFO_PROPS = ["name", "address", "locationId"];

export type {
	IReconciliation,
	IUnitOnLot,
	TFranchiseVehicleType,
	ISummaryDetail,
	IOnScheduledNotOnLot,
	IOnLotNotOnScheduled,
	IOnLotNotOnMfg,
	IOnMfgNotOnLot,
	IReconciliationNote,
	IReconciliationNoteHistory,
	IBasicUnitProperties,
	IUnitOnSchedule,
	IUnitOnMfg,
	IVehicleInfo,
	ILotInfo,
	IReconciliationStatus,
	IReconciliationReason,
	IReconciliationResolution,
	TReconciliationStatusType,
};

export { VEHICLE_INFO_PROPS, KEY_STATES_PROPS, LOT_INFO_PROPS, SPECIAL_VEHICLE_TYPES };
